import { apiClient as api } from '@plerionhq/design-system';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { buildGrantsQuery } from '../lib/grants-query';
import type { AccessGrant, ListGrantsResponse } from '../types';
import { useGrantsScope, type GrantsScope } from './use-grants-scope';
import { useListResourceTypes } from './use-list-resource-types';

const BASE_PATH = '/api/core/access/grants';

export async function fetchAllGrants(
  search: string,
  allResourceTypes: string[] | undefined,
  onProgress?: (fetched: number, total: number) => void,
  scope?: GrantsScope,
): Promise<AccessGrant[]> {
  const all: AccessGrant[] = [];
  let cursor: string | undefined;

  do {
    const params = buildGrantsQuery(search, {
      limit: 1000,
      cursor,
      allResourceTypes,
      scope,
    });

    const res = await api.get<ListGrantsResponse>(
      `${BASE_PATH}?${params.toString()}`,
    );
    all.push(...res.data.data);
    onProgress?.(all.length, res.data.meta.total);
    cursor = res.data.meta.nextCursor ?? undefined;
  } while (cursor);

  return all;
}

export function useListGrants(limit = 25) {
  const { search } = useLocation();
  const scope = useGrantsScope();
  const { data: allResourceTypes, isPending: resourceTypesPending } =
    useListResourceTypes();

  // Recognising a fully-covered Resource type filter needs the full list, so
  // hold the query until it lands — but only when the URL carries that filter,
  // so the common unfiltered load still paints on the first render.
  const usesResourceTypeFilter = new URLSearchParams(search).has('assetType');

  // Only the assetType path reads the resource-type list, so it belongs in the
  // key only there. Keying on it unconditionally refetched the whole list a
  // second time on every cold load, as the list resolved after the first fetch.
  // It has to stay in the key for that path: if the resource-type query failed
  // first, the grants request was built without the list and rejected as too
  // long, and a later success is what lets it recover.
  const resourceTypesKey = usesResourceTypeFilter
    ? (allResourceTypes?.length ?? 0)
    : 0;

  return useInfiniteQuery<ListGrantsResponse>({
    queryKey: ['access-grants', search, limit, resourceTypesKey, scope],
    enabled: !usesResourceTypeFilter || !resourceTypesPending,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const params = buildGrantsQuery(search, {
        limit,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
        allResourceTypes,
        scope,
      });

      const res = await api.get<ListGrantsResponse>(
        `${BASE_PATH}?${params.toString()}`,
      );
      return res.data;
    },
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor ?? undefined,
  });
}
